import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fullSync } from '../lib/sync'
import { useUIStore } from '../store/ui'
import { toast } from '../store/toast'
import { useNoteLimit } from './useNoteLimit'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

export interface ImportResult {
  imported: number
  skipped: number
  warnings: string[]
  source?: string
}

async function importFile(file: File): Promise<ImportResult> {
  const form = new FormData()
  form.append('file', file)

  // Don't use apiFetch here — it forces Content-Type: application/json which
  // breaks multipart/form-data (browser must set the boundary itself).
  const res = await fetch(`${API_URL}/notes/import`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  })

  if (!res.ok) {
    let message = `Import failed (${res.status})`
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // ignore
    }
    throw new Error(message)
  }

  return await res.json() as ImportResult
}

export function combineImportResults(results: ImportResult[]): ImportResult {
  return results.reduce<ImportResult>((acc, result) => ({
    imported: acc.imported + result.imported,
    skipped: acc.skipped + result.skipped,
    warnings: [...acc.warnings, ...result.warnings],
    source: acc.source ?? result.source,
  }), {
    imported: 0,
    skipped: 0,
    warnings: [],
    source: undefined,
  })
}

/**
 * Free note limit, applied to imports: how many of `total` files may go in.
 * One file counts as one note (a .zip / .bear2bk of many notes still counts as
 * one here: the server never rejects, so an overshoot just reads "56 of 50"
 * in the note list). Never negative; unlimited when `limit` is null.
 */
export function capImportFiles(count: number, limit: number | null, total: number): number {
  if (limit == null) return total
  return Math.max(0, Math.min(total, limit - count))
}

export function useImportNotes() {
  const [isPending, setIsPending] = useState(false)
  const { t } = useTranslation()
  const { count, limit } = useNoteLimit()

  const mutateAsync = async (files: File[]): Promise<ImportResult> => {
    // Import only up to the cap. At zero the paywall opens instead, the same
    // door `useCreateNote` shows; when some files fit, the "only X of N" toast
    // is shown after the import succeeds, so it never claims files were
    // imported when the import itself failed.
    const allowed = capImportFiles(count, limit, files.length)
    if (allowed === 0) {
      useUIStore.getState().openPaywall('note_limit')
      return { imported: 0, skipped: files.length, warnings: [] }
    }
    setIsPending(true)
    try {
      const results: ImportResult[] = []

      for (const file of files.slice(0, allowed)) {
        results.push(await importFile(file))
      }

      // Pull imported notes into local DB via sync
      await fullSync()
      if (allowed < files.length) {
        toast.warning(t('noteLimit.importCapped', { count: allowed, total: files.length, limit }))
      }
      return combineImportResults(results)
    } finally {
      setIsPending(false)
    }
  }

  return { mutateAsync, isPending }
}
